import { useEffect, useState } from 'react';
import { useUserStore } from '@/stores/userStore';
import { migrationApi } from '@/api/migration';
import { getCurrentUser } from 'aws-amplify/auth';

export function DataMigration() {
  const [status, setStatus] = useState<'idle' | 'checking' | 'migrating' | 'complete' | 'error'>('idle');
  const [error, setError] = useState('');
  const store = useUserStore();
  const setProfileId = useUserStore((state) => state.setProfileId);
  const enableBackend = useUserStore((state) => state.enableBackend);

  useEffect(() => {
    checkAndMigrate();
  }, []);

  async function checkAndMigrate() {
    // Check if backend is configured
    const hasBackendConfig = import.meta.env.VITE_USER_POOL_ID && import.meta.env.VITE_API_URL;
    if (!hasBackendConfig) {
      // No backend configured, skip migration
      setStatus('complete');
      return;
    }

    setStatus('checking');

    try {
      // Check if user is authenticated
      const user = await getCurrentUser();
      console.log('Current user:', user);

      if (!user) {
        console.log('No authenticated user found');
        setStatus('complete');
        return;
      }

      // Extract username from email
      const username = user.signInDetails?.loginId?.split('@')[0] || 'User';

      // Update profile to use Cognito user ID and username
      setProfileId(user.userId, username);

      // Enable backend sync
      enableBackend();

      // Check if already migrated
      const hasMigrated = localStorage.getItem('codequest_migrated');
      if (hasMigrated) {
        setStatus('complete');
        // Fetch data from backend
        await store.fetchProfile();
        await store.fetchProgress();
        await store.fetchAchievements();
        await store.fetchSettings();
        return;
      }

      // Get localStorage data
      const localDataStr = localStorage.getItem('codequest_user_v1');
      if (!localDataStr) {
        // No local data to migrate, just mark as complete
        localStorage.setItem('codequest_migrated', 'true');
        setStatus('complete');
        return;
      }

      setStatus('migrating');

      const localData = JSON.parse(localDataStr);
      const state = localData.state || localData;

      // Prepare migration data
      const migrationData = {
        profile: {
          userId: user.userId,
          username: state.profile?.username || user.username,
          xp: state.profile?.xp || 0,
          level: state.profile?.level || 1,
          createdAt: state.profile?.createdAt instanceof Date
            ? state.profile.createdAt.toISOString()
            : state.profile?.createdAt || new Date().toISOString(),
          lastActive: state.profile?.lastActive instanceof Date
            ? state.profile.lastActive.toISOString()
            : state.profile?.lastActive || new Date().toISOString(),
        },
        progress: {
          tutorialProgress: (state.progress?.tutorialProgress || []).map((p: any) => ({
            levelId: p.levelId,
            completed: p.completed,
            stars: p.stars || 0,
            attempts: p.attempts || 0,
            completedAt: p.completedAt instanceof Date
              ? p.completedAt.toISOString()
              : p.completedAt,
          })),
          challengeProgress: (state.progress?.challengeProgress || []).map((p: any) => ({
            challengeId: p.challengeId,
            completed: p.completed,
            bestScore: p.bestScore || 0,
            bestTime: p.bestTime || 0,
            attempts: p.attempts || 0,
            completedAt: p.completedAt instanceof Date
              ? p.completedAt.toISOString()
              : p.completedAt,
          })),
          escapeRoomProgress: (state.progress?.escapeRoomProgress || []).map((p: any) => ({
            roomId: p.roomId,
            completed: p.completed,
            bestTime: p.bestTime || 0,
            puzzlesCompleted: 0,
            completedAt: p.completedAt instanceof Date
              ? p.completedAt.toISOString()
              : p.completedAt,
          })),
          sandboxSessions: (state.progress?.sandboxSessions || []).map((s: any) => ({
            sessionId: s.id,
            name: s.name,
            templateId: s.template,
            createdAt: s.createdAt instanceof Date
              ? s.createdAt.toISOString()
              : s.createdAt,
            modifiedAt: s.modifiedAt instanceof Date
              ? s.modifiedAt.toISOString()
              : s.modifiedAt,
          })),
        },
        achievements: (state.achievements || []).map((a: any) => ({
          achievementId: a.id,
          name: a.name,
          description: a.description || '',
          icon: a.icon || '',
          unlockedAt: a.unlockedAt instanceof Date
            ? a.unlockedAt.toISOString()
            : a.unlockedAt || new Date().toISOString(),
          rarity: a.rarity || 'common',
        })),
        settings: {
          theme: state.settings?.theme || 'dark',
          soundEnabled: state.settings?.soundEnabled ?? true,
          musicEnabled: state.settings?.musicEnabled ?? false,
          animationsEnabled: state.settings?.animationsEnabled ?? true,
        },
      };

      // Send to migration endpoint
      const result = await migrationApi.migrateData(migrationData);

      if (result.success) {
        // Mark as migrated
        localStorage.setItem('codequest_migrated', 'true');

        // Fetch fresh data from server
        await store.fetchProfile();
        await store.fetchProgress();
        await store.fetchAchievements();
        await store.fetchSettings();

        setStatus('complete');
      } else {
        throw new Error('Migration failed');
      }
    } catch (err: any) {
      console.error('Migration error:', err);

      // If it's just an auth error (user not signed in), don't show error UI
      if (err.name === 'UserUnAuthenticatedException' || err.message?.includes('not authenticated')) {
        console.log('User not authenticated, skipping migration');
        setStatus('complete');
        return;
      }

      // Only show error UI for actual migration failures
      setError(err.message || 'Failed to migrate data');
      setStatus('error');
    }
  }

  if (status === 'idle' || status === 'complete') {
    return null;
  }

  if (status === 'checking') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Checking your data...</h2>
            <p className="text-gray-600">Please wait</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'migrating') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Migrating your data...</h2>
            <p className="text-gray-600">
              We're moving your progress, achievements, and settings to the cloud.
              This will only take a moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="text-center">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Migration Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
              <button
                onClick={() => setStatus('complete')}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
